import { awaitRedraw, el, inject, create } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { eventHandler, locate, locateNode, toNode } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { toDataDriver, toProxy } from "@dhx/ts-data";
import { ITreeGrid, TreeGrid } from "@dhx/ts-treegrid";
import { ILayout, Layout } from "@dhx/ts-layout";
import { Configurator, types } from "./configurator/Configurator";
import { Fields } from "./configurator/Fields";
import { DataPreprocessor } from "./DataPreprocessor";
import { getGridData } from "./group-module/DataGroup";
import { mathMethods } from "./group-module/groupfuncs";
import { cssManager } from "@dhx/ts-common/CssManager";
import locale from "./locales/en";
import {
	IFilterMeta,
	IParsedPivotConfig,
	IPivot,
	IPivotConfig,
	IPivotData,
	IPivotFields,
	mathMethod,
	PivotEvents,
	IShadow,
} from "./types";
import { getExporter } from "./Exporter";
import { Shadow } from "./Shadow";

export class Pivot extends View implements IPivot {
	public config: IParsedPivotConfig;
	public events: IEventSystem<PivotEvents>;
	public container: HTMLElement;
	public grid: ITreeGrid;
	public layout: ILayout;
	public export: any;
	public cssManager: any;

	private _configurator: Configurator;
	private _shadow: IShadow;
	private _dataPrep: DataPreprocessor;

	private _isSettingsHidden = false;
	private _htmlEvents: any;

	constructor(container: HTMLElement | string, config: IPivotConfig) {
		super(container, config);
		this.container = container === null ? (container as any) : toNode(container);

		this._htmlEvents = {
			onmousedown: eventHandler(ev => locate(ev), {
				dhx_config_item: (ev, item) => {
					const fieldNode = locateNode(ev.target) as HTMLElement;
					this._configurator.setPressedItem(ev, item, fieldNode);
				},
			}),
			ontouchstart: eventHandler(ev => locate(ev), {
				dhx_config_item: (ev, item) => {
					const fieldNode = locateNode(ev.target) as HTMLElement;
					this._configurator.setPressedItem(ev, item, fieldNode);
				},
			}),
			onclick: eventHandler(ev => locate(ev), {
				pivot_settings_btn: () => {
					this._isSettingsHidden = !this._isSettingsHidden;
					this._checkLayoutState();
				},
				dhx_config_select: (ev, item) => {
					this.events.fire(PivotEvents.fieldClick, [ev, item, "selector"]);
				},
				dhx_close: (ev, item) => {
					this.events.fire(PivotEvents.fieldClick, [ev, item, "close"]);
				},
				item_body: (ev, item) => {
					this.events.fire(PivotEvents.fieldClick, [ev, item, "filter"]);
				},
				dhx_button: (_ev, item) => {
					if (item === "dhx_configApplyBtn") {
						this.events.fire(PivotEvents.applyButtonClick);
					}
				},
			}),
		};

		this._dataPrep = new DataPreprocessor();
		this.cssManager = cssManager;

		this.config = this._dataPrep.normalizeConfig(config);
		if (this.config.layout.gridOnly) {
			this._isSettingsHidden = true;
		}
		types.operations = this.config.types.operations || types.operations;
		this._init();

		if (this.config.data) {
			this.setData(this.config.data);
		}

		this._update();
		this._setEventsHandlers();

		this._initLayout();
		this._checkLayoutState();

		const gridRoot = this.grid.getRootView();
		gridRoot.hooks = gridRoot.hooks || {};
		gridRoot.hooks.didMount = () => {
			this.container = this.layout.getRootView().node.el;
		};

		this.export = getExporter(this);
		this._shadow = new Shadow(this._configurator);

		const view = create({ render: () => this._render() });
		this.mount(container, view);
	}

	destructor() {
		this._shadow.destructor();
		this.layout.destructor();
		this.grid.destructor();
		this.events.clear();
		this.unmount();
	}

	setFields(fields: IPivotFields) {
		this.config.fields = fields;
		this._dataPrep.normalizeFields(this.config);
		this.events.fire(PivotEvents.change);
	}

	getFields(): IPivotFields {
		return this._dataPrep.getPureFields(this.config.fields);
	}

	getConfig(): IPivotConfig {
		return this.config;
	}

	load(url: string, type?: string) {
		const proxy = toProxy(url);
		return proxy.load<any>().then(data => this.setData(data, type));
	}

	setData(data: IPivotData, type?: any) {
		const driver = toDataDriver(type || "json");
		data = driver.toJsonArray(data);

		this.config.data = this._dataPrep.normalizeData(data, this.config.fieldList);
		this.events.fire(PivotEvents.change);
	}

	addMathMethod(id: string, label: string, func: mathMethod) {
		mathMethods[id] = func;
		locale[id] = label;
		types.operations.push({ id, label });

		this.grid.content[id] = {
			toHtml(column, config) {
				return this.calculate(column, config);
			},
			calculate(column: any, config: any) {
				const columnData = config.data.map(item =>
					item.$items && item.$opened ? "0" : item[column.id] || "0"
				);
				return func(columnData);
			},
		};
	}

	addSubField(name: string, functor: any, label: string) {
		locale[label] = locale[label] ? locale[label] : label;
		this._dataPrep.addSubField(name, functor, label);
		this.paint();
		setTimeout(() => {
			this.events.fire(PivotEvents.change);
		}, 100);
	}

	setFilterValue(field: string, meta: IFilterMeta) {
		this._dataPrep.setFilterValue(field, meta);
		this.events.fire(PivotEvents.change);
	}

	setFiltersValues(filters: { [key: string]: IFilterMeta }) {
		this._dataPrep.setFiltersValues(filters);
		this.events.fire(PivotEvents.change);
	}

	getFilterValue(fieldId: string) {
		return this._dataPrep.getFilterValue(fieldId);
	}

	getFiltersValues() {
		return this._dataPrep.getFiltersValues();
	}

	clearFiltersValues() {
		this._dataPrep.clearFiltersValues();
		this.events.fire(PivotEvents.change);
	}

	setGlobalFilter(handler?: any) {
		this._dataPrep.setGlobalFilter(handler);
		this.events.fire(PivotEvents.change);
	}

	paint() {
		super.paint();
		if (this._shadow) {
			this._shadow.paint();
		}
		if (this.layout) {
			this.layout.paint();
		}
	}

	addFilter() {
		alert("method is deprecated, see docs https://docs.dhtmlx.com/pivot/api__refs__pivot.html");
	}

	removeFilter() {
		alert("method is deprecated, see docs https://docs.dhtmlx.com/pivot/api__refs__pivot.html");
	}

	getFilter() {
		alert("method is deprecated, see docs https://docs.dhtmlx.com/pivot/api__refs__pivot.html");
	}

	private _setEventsHandlers() {
		this.events.on(PivotEvents.change, () => {
			if (this.config.layout.liveReload !== false) {
				this._update();
			}
			this.paint();
		});
		this.events.on(PivotEvents.update, () => {
			this.paint();
		});
		this.events.on(PivotEvents.applyButtonClick, () => {
			this._update();
			this.paint();
		});
		this.events.on(PivotEvents.filterApply, (field: string, meta: IFilterMeta) => {
			this.setFilterValue(field, meta);
		});
	}

	private _update() {
		const fields = this.config.fields;
		if (!fields.rows.length) {
			return;
		}

		const gridInfo = getGridData(
			{ ...this.config, fields },
			this._dataPrep.getData(this.config.data || [], fields),
			this.grid
		);
		const preParseData = JSON.parse(JSON.stringify(gridInfo.groupedData));
		this.grid.setColumns(gridInfo.headers);
		this.grid.data.parse(gridInfo.groupedData);
		if (this.config.layout.gridMode === "flat") {
			this.grid.data.forEach(item => {
				item.$level = preParseData.find(i => i.id === item.id).$level;
			});
		}

		if (this.config.layout.columnsWidth === "auto") {
			this.grid.config.columns.forEach(col => {
				this.grid.adjustColumnWidth(col.id);
			});
		} else if (!this.config.layout.columnsWidth) {
			this.grid.adjustColumnWidth(this.grid.config.columns[0].id);
			if (this.config.layout.gridMode === "flat") {
				this.grid.adjustColumnWidth(this.grid.config.columns[1].id);
			}
		}
	}

	private _init() {
		this.events = new EventSystem(this);

		this.grid = new TreeGrid(null, {
			data: [],
			columns: [],
			type: "tree",
			width: 400,
			height: 400,
			htmlEnable: true,
		});

		this._configurator = new Configurator(this, {
			...this.config,
			events: this.events,
		});
	}

	private _initLayout() {
		const columnsStyle = this.cssManager.add({
			paddingLeft: `${(this.config.layout.leftMargin || 168) + 12}px`,
		});

		const rowsStyle = this.cssManager.add({
			minWidth: `${this.config.layout.leftMargin || 168}px`,
		});

		const pivotHeader = [
			{ id: "freeFields", css: "dhx_free-fields-wrap dhx_config_free-items" },
			{ id: "hideSettings", css: "dhx_settings-button-wrap", width: "content" },
		];

		if (!this.config.layout.liveReload) {
			pivotHeader.splice(1, 0, { id: "manualApply", css: "dhx_apply-button-wrap" } as any);
		}

		const layout = (this.layout = new Layout(null, {
			views: [
				{
					css: "dhx_config",
					id: "full",
					rows: [
						{
							id: "pivotHeader",
							css: "dhx_pivot-header",
							cols: pivotHeader,
							height: "content",
						},
						{
							id: "mainArea",
							css: "dhx_pivot-main-area",
							rows: [
								{
									id: "valuesFields",
									css: "dhx_values-fields-wrap",
									height: "content",
								},
								{
									id: "columnsFields",
									css: `dhx_columns-fields-wrap ${columnsStyle}`,
									height: "content",
								},
								{
									id: "gridWrapper",
									css: "dhx_pivot-grid-wrap",
									cols: [
										{
											id: "rowsFields",
											css: `dhx_rows-fields-wrap ${rowsStyle}`,
											width: "content",
										},
										{
											id: "grid",
											css: "dhx_pivot-grid",
										},
									],
								},
							],
						},
					],
				},
				{
					css: "dhx_pivot--readonly",
					id: "gridOnly",
					rows: [
						{
							id: "gridWrapper",
							css: "dhx_pivot-grid-wrap",
							rows: [
								{
									id: "showSettings",
									css: "dhx_settings-button-wrap dhx_settings-button-wrap--readonly",
									height: "content",
								},
								{
									id: "gridOnlyWrap",
									css: "dhx_pivot-grid",
								},
							],
						},
					],
				},
			],
		}));

		const free = new Fields(null, {
			configurator: this._configurator,
			item: "free",
			events: this._htmlEvents,
		});

		const values = new Fields(null, {
			configurator: this._configurator,
			item: "values",
			events: this._htmlEvents,
		});

		const cols = new Fields(null, {
			configurator: this._configurator,
			item: "columns",
			events: this._htmlEvents,
		});

		const rows = new Fields(null, {
			configurator: this._configurator,
			item: "rows",
			events: this._htmlEvents,
		});

		layout.getCell("grid").attach(this.grid, this);
		layout.getCell("freeFields").attach(free, this);
		layout.getCell("valuesFields").attach(values, this);
		layout.getCell("columnsFields").attach(cols, this);
		layout.getCell("rowsFields").attach(rows, this);
		layout.getCell("gridOnlyWrap").attach(this.grid, this);

		const getSettingsButton = (type: "hide" | "show") => ({
			getRootView: () =>
				el(
					"button",
					{
						class:
							"pivot_settings_btn dhx_button dhx_button--view_link dhx_button--color_primary dhx_button--size_medium",
						...this._htmlEvents,
						type: "button",
					},
					locale[type === "hide" ? "hideSettings" : "showSettings"]
				),
		});

		const getManualApplyButton = (type: boolean) => ({
			getRootView: () =>
				el(
					"button",
					{
						class:
							"dhx_pivot-apply-button dhx_button dhx_button--view_flat dhx_button--color_primary dhx_button--size_medium",
						dhx_id: "dhx_configApplyBtn",
						...this._htmlEvents,
						type: "button",
					},
					[el("i.dxi.dxi-check"), el("span", locale.apply)]
				),
		});

		layout.getCell("showSettings").attach(getSettingsButton("show"), this);
		layout.getCell("hideSettings").attach(getSettingsButton("hide"), this);

		if (this.config.layout.liveReload === false) {
			layout.getCell("manualApply").attach(getManualApplyButton(this.config.layout.liveReload), this);
		}
	}

	private _checkLayoutState() {
		if (this.config.layout.readonly) {
			this.layout.getCell("gridOnly").show();
			this.layout.getCell("showSettings").hide();
			return;
		}

		if (this._isSettingsHidden) {
			this.layout.getCell("gridOnly").show();
		} else {
			this.layout.getCell("full").show();
		}

		awaitRedraw().then(() => {
			const scroll = this.grid.getScrollState();
			this.grid.scroll(scroll.x, scroll.y);
			this.grid.paint();
		});
	}

	private _render() {
		return el(
			".dhx_widget.dhx_pivot",
			{
				dhx_widget_id: this._uid,
				_key: this._uid,
			},
			[inject(this.layout.getRootView())]
		);
	}
}
